import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import {
  templateDtoToFormState,
  type TemplateFormState,
} from "./template-management-form.js";

const SUPPORTED_BUTTON_TYPES = new Set([
  "QUICK_REPLY",
  "URL",
  "PHONE_NUMBER",
  "PHONE",
  "VOICE_CALL",
]);

const SUPPORTED_COMPONENT_TYPES = new Set(["HEADER", "BODY", "FOOTER", "BUTTONS"]);

export type TemplateEditSurface =
  | { kind: "marketing_default_editor" }
  | { kind: "legacy_editor" }
  | { kind: "unsupported"; reasons: readonly TemplateEditUnsupportedReason[] };

export type TemplateEditUnsupportedReason =
  | "media_header"
  | "unsupported_button"
  | "unsupported_component"
  | "hydration_loss"
  | "authentication_category";

/** Which React surface TemplateManagementSheet must mount for an edit. */
export type TemplateEditSheetPresentation =
  | "marketing_default_editor"
  | "unsupported"
  | "legacy_form";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function componentType(component: Record<string, unknown>): string {
  return typeof component.type === "string" ? component.type.trim().toUpperCase() : "";
}

function headerFormat(component: Record<string, unknown>): string {
  return typeof component.format === "string" ? component.format.trim().toUpperCase() : "";
}

function buttonType(button: Record<string, unknown>): string {
  return typeof button.type === "string" ? button.type.trim().toUpperCase() : "";
}

/**
 * True when Meta components can round-trip through the MARKETING_DEFAULT Step-2 editor
 * (TEXT header / body / footer + supported buttons only).
 */
export function hasMarketingDefaultEditorCompatibleComponents(
  components: unknown,
): boolean {
  return collectUnsupportedReasonsFromComponents(components).length === 0;
}

function collectUnsupportedReasonsFromComponents(
  components: unknown,
): TemplateEditUnsupportedReason[] {
  const reasons = new Set<TemplateEditUnsupportedReason>();
  if (!Array.isArray(components)) {
    return ["unsupported_component"];
  }

  let metaButtonCount = 0;
  let parsedButtonCount = 0;

  for (const entry of components) {
    const component = asRecord(entry);
    if (!component) {
      reasons.add("unsupported_component");
      continue;
    }
    const type = componentType(component);
    if (!SUPPORTED_COMPONENT_TYPES.has(type)) {
      reasons.add("unsupported_component");
      continue;
    }

    if (type === "HEADER") {
      const format = headerFormat(component);
      if (format === "VIDEO" || format === "DOCUMENT" || format === "LOCATION") {
        reasons.add("media_header");
      } else if (format === "IMAGE") {
        // IMAGE is editable in the default wizard (sample already on Meta).
      } else if (format && format !== "TEXT") {
        reasons.add("unsupported_component");
      } else if (format === "TEXT" && typeof component.text !== "string") {
        reasons.add("unsupported_component");
      }
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      for (const buttonEntry of component.buttons) {
        metaButtonCount += 1;
        const button = asRecord(buttonEntry);
        if (!button) {
          reasons.add("unsupported_button");
          continue;
        }
        const typeName = buttonType(button);
        if (!SUPPORTED_BUTTON_TYPES.has(typeName)) {
          reasons.add("unsupported_button");
          continue;
        }
        parsedButtonCount += 1;
      }
    }
  }

  if (metaButtonCount !== parsedButtonCount) {
    reasons.add("unsupported_button");
  }

  return [...reasons];
}

function collectUnsupportedReasons(template: WhatsAppTemplateDto): TemplateEditUnsupportedReason[] {
  const reasons = new Set<TemplateEditUnsupportedReason>(
    collectUnsupportedReasonsFromComponents(template.components),
  );

  // Hydrate and compare: dropped media headers / unknown buttons must not open the editor.
  const hydrated = templateDtoToFormState(template);
  if (Array.isArray(template.components)) {
    for (const entry of template.components) {
      const component = asRecord(entry);
      if (!component) continue;
      if (componentType(component) !== "HEADER") continue;
      const format = headerFormat(component);
      if (
        (format === "VIDEO" || format === "DOCUMENT" || format === "LOCATION") &&
        !hydrated.hasHeader
      ) {
        reasons.add("hydration_loss");
      }
      if (format === "IMAGE" && hydrated.headerKind !== "IMAGE") {
        reasons.add("hydration_loss");
      }
      if (format === "TEXT" && typeof component.text === "string" && component.text.length > 0) {
        if (!hydrated.hasHeader || hydrated.headerText !== component.text) {
          reasons.add("hydration_loss");
        }
      }
    }

    let metaButtonCount = 0;
    for (const entry of template.components) {
      const component = asRecord(entry);
      if (!component || componentType(component) !== "BUTTONS") continue;
      if (Array.isArray(component.buttons)) metaButtonCount += component.buttons.length;
    }
    if (metaButtonCount > 0 && hydrated.buttons.length !== metaButtonCount) {
      reasons.add("hydration_loss");
    }
  }

  return [...reasons];
}

/**
 * Decide which edit surface a cached template may use.
 *
 * MARKETING and UTILITY templates whose components fit the default Step-2 editor
 * share that same Create UI. Unsupported shapes are blocked (no destructive edit).
 * AUTHENTICATION stays off the default editor.
 */
export function resolveTemplateEditSurface(template: WhatsAppTemplateDto): TemplateEditSurface {
  const category = template.category.trim().toUpperCase();
  if (category === "AUTHENTICATION") {
    return { kind: "unsupported", reasons: ["authentication_category"] };
  }

  const reasons = collectUnsupportedReasons(template);
  if (reasons.length > 0) {
    return { kind: "unsupported", reasons };
  }

  // Compatible MARKETING / UTILITY (and unknown categories with safe shape) use Create Step 2.
  if (category === "MARKETING" || category === "UTILITY" || category.length > 0) {
    return { kind: "marketing_default_editor" };
  }

  return { kind: "legacy_editor" };
}

export function isMarketingDefaultEditorTemplate(template: WhatsAppTemplateDto): boolean {
  return resolveTemplateEditSurface(template).kind === "marketing_default_editor";
}

function hasImageHeader(components: unknown): boolean {
  if (!Array.isArray(components)) return false;
  for (const entry of components) {
    const component = asRecord(entry);
    if (!component || componentType(component) !== "HEADER") continue;
    if (headerFormat(component) === "IMAGE") return true;
  }
  return false;
}

/**
 * Duplicate opens a new create draft. IMAGE headers are excluded because this
 * phase does not copy durable media. Authentication and other unsupported
 * shapes are excluded so we do not invent a lossy conversion.
 */
export function canDuplicateTemplate(template: WhatsAppTemplateDto): boolean {
  const category = template.category.trim().toUpperCase();
  if (category !== "MARKETING" && category !== "UTILITY") return false;
  if (hasImageHeader(template.components) || template.headerMedia != null) return false;
  return resolveTemplateEditSurface(template).kind === "marketing_default_editor";
}

export function buildDuplicateTemplateFormState(
  template: WhatsAppTemplateDto,
): TemplateFormState | null {
  if (!canDuplicateTemplate(template)) return null;
  const hydrated = templateDtoToFormState(template);
  return {
    ...hydrated,
    name: "",
    headerImageFile: null,
    headerImagePreviewUrl: null,
    hasStoredHeaderImage: false,
  };
}

/** Sheet mount kind — preferred over ad-hoc boolean flags in the sheet. */
export function resolveTemplateEditSheetPresentation(
  template: WhatsAppTemplateDto,
): TemplateEditSheetPresentation {
  const surface = resolveTemplateEditSurface(template);
  switch (surface.kind) {
    case "marketing_default_editor":
      return "marketing_default_editor";
    case "unsupported":
      return "unsupported";
    case "legacy_editor":
      return "legacy_form";
    default: {
      const _exhaustive: never = surface;
      return _exhaustive;
    }
  }
}
